import { ProCard, ProList } from '@ant-design/pro-components'
import { localQueryEntityOptions } from '@app/icare/client/db/client/localQueryOneOptions'
import { sync } from '@app/icare/client/sync'
import { IkNumberSchema, IkNumberValidationIssueEnum } from '@app/icare/db/schema/common/ik-number'
import type { CostCarrier } from '@app/icare/entities/costCarrier/schema/CostCarrier'
import { t } from '@lingui/core/macro'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Button, Form, Input, Popconfirm, Space, Typography } from 'antd'
import { useMemo, useState } from 'react'
import { FaSearch } from 'react-icons/fa'
import { FaPlus, FaTrash } from 'react-icons/fa6'

export const Route = createFileRoute('/_app/kostentrager/$id/ik-numbers')({
  component: RouteComponent,
})

type IkNumberEntry = CostCarrier['ikNumber']['secondary'][number]

function RouteComponent() {
  const params = Route.useParams()
  const { data } = useQuery(localQueryEntityOptions(params.id))
  const [filter, setFilter] = useState('')

  const secondary = data?.ikNumber.secondary
  const filteredIkNumbers = useMemo(
    () => secondary?.filter(entry => entry.ikNumber.startsWith(filter)) ?? [],
    [secondary, filter],
  )
  const validationStatus: { status: 'success' | 'error' | ''; message?: string } = useMemo(() => {
    if (filter === '') return { status: '', message: undefined }

    const validationResult = IkNumberSchema.safeParse(filter)
    if (validationResult.success) return { status: 'success', message: undefined }

    const firstIssueMessage = validationResult.error.issues[0]?.message as
      | typeof IkNumberValidationIssueEnum.$.Type
      | undefined
    const issueLabel = IkNumberValidationIssueEnum.$.getLabel(firstIssueMessage) ?? ''
    return { status: 'error', message: issueLabel }
  }, [filter])

  if (!data) {
    return <ProCard loading />
  }

  const addingDisabled = filter === '' || filteredIkNumbers.length > 0 || validationStatus.status !== 'success'

  const persist = (next: CostCarrier['ikNumber']['secondary']) => {
    void sync.mutate({ id: data.id, ikNumber: { ...data.ikNumber, secondary: next } })
  }

  const handleAdd = () => {
    if (addingDisabled) return

    persist([...(secondary ?? []), { ikNumber: filter, address: null }])
    setFilter('')
  }

  const handleDelete = (ikNumber: string) => {
    persist((secondary ?? []).filter(entry => entry.ikNumber !== ikNumber))
  }

  return (
    <ProCard
      title={t`IK-Nummern`}
      headerBordered
      size="small"
      extra={
        <Form.Item
          style={{ marginBottom: 0, display: 'flex', justifyContent: 'flex-end' }}
          help={validationStatus.status === 'error' && validationStatus.message ? validationStatus.message : undefined}
          validateStatus={validationStatus.status}
        >
          <Space.Compact size="small">
            <Space.Addon>
              <FaSearch />
            </Space.Addon>
            <Input
              placeholder={t`IK-Nummern durchsuchen`}
              onChange={e => setFilter(e.target.value)}
              value={filter}
              status={validationStatus.status}
              allowClear
              maxLength={9}
              showCount
            />
            <Button icon={<FaPlus />} disabled={addingDisabled} onClick={handleAdd} variant="solid" color="primary">
              {t`IK-Nummer hinzufügen`}
            </Button>
          </Space.Compact>
        </Form.Item>
      }
    >
      <ProList<IkNumberEntry>
        dataSource={filteredIkNumbers}
        rowKey="ikNumber"
        size="small"
        split
        ghost
        columns={[
          {
            listSlot: 'title',
            render: (_, { ikNumber }) => <Typography.Text>{ikNumber}</Typography.Text>,
          },
          {
            listSlot: 'actions',
            render: (_, { ikNumber }) => (
              <Popconfirm
                title={t`IK-Nummer wirklich löschen?`}
                okText={t`Löschen`}
                okButtonProps={{ danger: true }}
                cancelText={t`Abbrechen`}
                onConfirm={() => handleDelete(ikNumber)}
              >
                <Button type="text" danger icon={<FaTrash />} />
              </Popconfirm>
            ),
          },
        ]}
      />
    </ProCard>
  )
}
